import React from "react";
import { Spin } from "antd";
import { TFunction } from "i18next";
import { CSSProperties } from "react";

export const EXECUTION_END_MARKER = "--- Fin de ejecución ---";

export interface TaskLogLine {
  timestamp: number;
  message: string;
}

export function isTerminationFooterLine(message?: string | null): boolean {
  return !!message && message.startsWith(EXECUTION_END_MARKER);
}

export function getTerminationFooterStartIndex(logs: TaskLogLine[]): number {
  return logs.findIndex((log) => isTerminationFooterLine(log.message));
}

export function isInTerminationFooter(logs: TaskLogLine[], index: number): boolean {
  const footerStart = getTerminationFooterStartIndex(logs);
  return footerStart >= 0 && index >= footerStart;
}

const baseTimestampStyle: CSSProperties = {
  color: "#007bff",
  marginRight: "12px",
  minWidth: "140px",
  fontSize: "11px",
};

const baseMessageStyle: CSSProperties = {
  color: "#495057",
  flex: 1,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};

interface TaskLogLinesProps {
  logs: TaskLogLine[];
  logsLoading: boolean;
  nextToken?: string;
  formatTimestamp: (timestamp: number) => string;
  t: TFunction;
}

const TaskLogLines: React.FC<TaskLogLinesProps> = ({
  logs,
  logsLoading,
  nextToken,
  formatTimestamp,
  t,
}) => {
  if (logsLoading && logs.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "20px", color: "#6c757d" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (logs.length === 0 && !logsLoading) {
    return (
      <div style={{ textAlign: "center", padding: "20px", color: "#6c757d" }}>
        {t("DataScienceJobs-NoLogsFound")}
      </div>
    );
  }

  return (
    <div>
      {logs.map((log, index) => {
        const isFooter = isInTerminationFooter(logs, index);
        return (
          <div
            key={`${log.timestamp}-${index}`}
            style={{
              marginBottom: "2px",
              padding: "2px 0",
              borderBottom: "1px solid #dee2e6",
              display: "flex",
              alignItems: "flex-start",
              backgroundColor: isFooter ? "#fff5f5" : undefined,
            }}
          >
            <span
              style={{
                ...baseTimestampStyle,
                ...(isFooter
                  ? { color: "#cf1322", fontWeight: 600 }
                  : undefined),
              }}
            >
              {formatTimestamp(log.timestamp)}
            </span>
            <span
              style={{
                ...baseMessageStyle,
                ...(isFooter
                  ? { color: "#cf1322", fontWeight: 600 }
                  : undefined),
              }}
            >
              {log.message}
            </span>
          </div>
        );
      })}
      {logsLoading && nextToken && logs.length > 0 && (
        <div
          style={{
            textAlign: "center",
            padding: "10px",
            color: "#6c757d",
            fontStyle: "italic",
          }}
        >
          {t("DataScienceJobs-LoadingMoreLogs")}
        </div>
      )}
    </div>
  );
};

export default TaskLogLines;

export function canViewTaskLogs(task: {
  has_logs?: boolean;
  status?: string;
  message?: string;
}): boolean {
  return (
    task.has_logs === true ||
    (task.status === "failed" && !!task.message) ||
    task.status === "success"
  );
}
